import { HttpClient } from '../shared/api/HttpClient';
import type { TrackSummary } from '../segue/types';
import type { FutureSchedule, SchedulerConfiguration, StationSummary } from './types';

interface StationPage {
  items: StationSummary[];
  nextCursor: string | null;
}

interface TrackPage {
  items: TrackSummary[];
  nextCursor: string | null;
}

export class SchedulerApi {
  readonly #http: HttpClient;

  public constructor(http = new HttpClient({ readCsrfToken: readCsrfCookie })) {
    this.#http = http;
  }

  public async stations(): Promise<StationSummary[]> {
    return (await this.#http.request<StationPage>('/stations?limit=100')).items;
  }

  public async tracks(): Promise<TrackSummary[]> {
    const tracks: TrackSummary[] = [];
    let cursor: string | null = null;
    do {
      const page: TrackPage = await this.#http.request(`/tracks?limit=100${cursor === null ? '' : `&cursor=${encodeURIComponent(cursor)}`}`);
      tracks.push(...page.items);
      cursor = page.nextCursor;
    } while (cursor !== null);
    return tracks;
  }

  public configuration(stationId: string): Promise<SchedulerConfiguration> {
    return this.#http.request(`/stations/${stationId}/scheduler-configuration`);
  }

  public save(configuration: SchedulerConfiguration): Promise<SchedulerConfiguration> {
    const { stationId, stationRevision, ...payload } = configuration;

    return this.#http.request(`/stations/${stationId}/scheduler-configuration`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'If-Match': `"${stationRevision}"`,
      },
      body: JSON.stringify(payload),
    });
  }

  public future(stationId: string): Promise<FutureSchedule> {
    return this.#http.request(`/stations/${stationId}/future-schedule`);
  }

  public createManualInsert(stationId: string, trackId: string, scheduledAt: string): Promise<unknown> {
    return this.#http.request(`/stations/${stationId}/manual-inserts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: crypto.randomUUID(), trackId, scheduledAt }),
    });
  }

  public createExactEvent(stationId: string, trackId: string, exactAt: string, toleranceMs: number): Promise<unknown> {
    return this.#http.request(`/stations/${stationId}/exact-events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: crypto.randomUUID(),
        trackId,
        exactAt,
        toleranceBeforeMs: toleranceMs,
        toleranceAfterMs: toleranceMs,
        precedence: 0,
        transitionProfileId: null,
      }),
    });
  }
}

function readCsrfCookie(): string | null {
  const prefix = '__Host-radio_csrf=';
  const value = document.cookie.split(';').map((part) => part.trim()).find((part) => part.startsWith(prefix));

  return value === undefined ? null : decodeURIComponent(value.slice(prefix.length));
}
